import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Moment from 'moment';
import 'moment/locale/ru';

import '../Styles/home.css';
import Competition from './Competition';
import Rules from './Rules.jsx';
import UserlogItem from './userlogItem';

Moment.locale('ru');

// ---- Formatters
const fmtNum = (n) =>
    Intl.NumberFormat('ru-RU', { style: 'decimal', minimumFractionDigits: 0 }).format(Number(n || 0));

const fmtRub = (n) =>
    Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 }).format(Number(n || 0));

const initialUserData = {
    ID: '',
    Name: '',
    userPosition: '',
    TotalDebt: 0,
    AZS: '',
    OperatorID: '',
    isOnlyGas: 0,
    SalaryBySales: 1,
};

const initialUserSalary = {
    Rate: 0.0,
    Litres: 0.0,
    Salary: 0.0,
    Bonus: 0.0,
    Prepayment: 0.0,
    Fees: 0.0,
    Minus: 0.0,
    Debt: 0.0,
    TotalSalary: 0.0,
    isSpecialRateForCash: 0.0,
    SpecialRateForCash: 0.0,
    LitresCash: 0.0,
    LitresNonCash: 0.0,
};

const initialLogs = [
    { Date_Created: '...', Payment_type: '...', Amount: 0, comment: '...' },
];

function Home() {
    const navigate = useNavigate();
    const urlPath = 'https://rusoil.online';

    // ---- State
    const [userData, setUserData] = useState(initialUserData);
    const [userSalary, setUserSalary] = useState(initialUserSalary);
    const [userLogs, setUserLogs] = useState(initialLogs);

    const [isCurrMonth, setIsCurrMonth] = useState(true);
    const [monthLabel, setMonthLabel] = useState(Moment(new Date()).format('MMM YYYY'));

    const [loadingUser, setLoadingUser] = useState(false);
    const [loadingSalary, setLoadingSalary] = useState(false);
    const [loadingLogs, setLoadingLogs] = useState(false);

    const [errUser, setErrUser] = useState('');
    const [errSalary, setErrSalary] = useState('');
    const [errLogs, setErrLogs] = useState('');

    // Derived flags
    const hasCashRate = useMemo(
        () => Number(userSalary?.isSpecialRateForCash || 0) === 1 && Number(userSalary?.SpecialRateForCash || 0) > 0,
        [userSalary?.isSpecialRateForCash, userSalary?.SpecialRateForCash]
    );

    const hasDebt = useMemo(
        () => Number(userData.TotalDebt || 0) !== 0,
        [userData.TotalDebt]
    );

    // ---- Common fetch helper
    const doFetch = useCallback(async (path, { signal } = {}) => {
        const headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            // NOTE: keep your Basic header as-is (ideally move to server-side session in future)
            Authorization: 'Basic aipfYXV0aF9mZiVnZyQ6XiVoZmo5SGRqJkBuaEoxMjMwVmJjc2NzYWZ1XiViRA==',
        };
        const res = await fetch(`${urlPath}${path}`, {
            method: 'GET',
            mode: 'same-origin',
            credentials: 'include',
            headers,
            signal,
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
    }, []);

    // ---- User info
    const getUserInfo = useCallback(async (signal) => {
        setLoadingUser(true);
        setErrUser('');
        try {
            const data = await doFetch('/webapi/secured/getuserinfo', { signal });
            setUserData(data || initialUserData);
        } catch (e) {
            if (e.name !== 'AbortError') setErrUser('Ошибка загрузки данных пользователя');
        } finally {
            setLoadingUser(false);
        }
    }, [doFetch]);

    // ---- Salary + Logs (parallel)
    const getUserSalary = useCallback(async (signal, current) => {
        setLoadingSalary(true);
        setErrSalary('');
        try {
            const data = await doFetch(`/webapi/secured/GetSalary?isCurrentMonth=${current}`, { signal });
            setUserSalary(data || initialUserSalary);
        } catch (e) {
            if (e.name !== 'AbortError') setErrSalary('Ошибка загрузки зарплаты');
        } finally {
            setLoadingSalary(false);
        }
    }, [doFetch]);

    const getUserLogs = useCallback(async (signal, current) => {
        setLoadingLogs(true);
        setErrLogs('');
        try {
            const headers2 = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Basic cnVzb2lsQXBwOnJ1c3NvaWw1NDQ2NTc2', // your original logs token
            };
            const res = await fetch(
                `${urlPath}/webapi/secured/getUserLogs?isCurrentMonth=${current}&manualDate=false`,
                { method: 'GET', mode: 'same-origin', credentials: 'include', headers: headers2, signal }
            );
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
            const data = await res.json();
            setUserLogs(Array.isArray(data) ? data : initialLogs);
        } catch (e) {
            if (e.name !== 'AbortError') setErrLogs('Ошибка загрузки записей');
        } finally {
            setLoadingLogs(false);
        }
    }, [urlPath]);

    const getUserStatistic = useCallback(async (signal, current) => {
        await Promise.allSettled([
            getUserSalary(signal, current),
            getUserLogs(signal, current),
        ]);
    }, [getUserSalary, getUserLogs]);

    // ---- Effects
    // 1) Load user info once on mount
    useEffect(() => {
        const ctrl = new AbortController();
        getUserInfo(ctrl.signal);
        return () => ctrl.abort();
    }, [getUserInfo]);

    // 2) Whenever month selection changes, refresh salary + logs
    useEffect(() => {
        const ctrl = new AbortController();
        setUserSalary(initialUserSalary); // quick reset UI
        setUserLogs(initialLogs);
        getUserStatistic(ctrl.signal, isCurrMonth);
        return () => ctrl.abort();
    }, [isCurrMonth, getUserStatistic]);

    // 3) Keep Month label in sync with toggle
    useEffect(() => {
        setMonthLabel(
            isCurrMonth
                ? Moment(new Date()).format('MMM YYYY')
                : Moment(new Date()).subtract(1, 'months').format('MMM YYYY')
        );
    }, [isCurrMonth]);

    // ---- UI Handlers
    const prevMonthClicked = useCallback(() => {
        setIsCurrMonth(false);
    }, []);

    const currentMonthClicked = useCallback(() => {
        setIsCurrMonth(true);
    }, []);

    const SignOut = useCallback(async () => {
        try {
            const res = await fetch(`${urlPath}/webapi/secured/logout`, {
                method: 'POST',
                mode: 'same-origin',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Basic aipfYXV0aF9mZiVnZyQ6XiVoZmo5SGRqJkBuaEoxMjMwVmJjc2NzYWZ1XiViRA==',
                },
            });
            if (!res.ok) {
                console.error('Logout failed:', res.statusText);
            }
            navigate('/login', { replace: true });
        } catch (err) {
            console.error('Error during logout:', err);
        }
    }, [navigate, urlPath]);

    // ---- Button active classes (no manual style mutation)
    const prevBtnClass = useMemo(
        () => `btn01 ${!isCurrMonth ? 'active' : ''}`,
        [isCurrMonth]
    );
    const currBtnClass = useMemo(
        () => `btn02 ${isCurrMonth ? 'active' : ''}`,
        [isCurrMonth]
    );

    // ---- Render
    return (
        <div className="Home">
            <b id="rusoil">RUSOIL</b>

            <div className="maindata">
                <Competition />
                <div id="exiteblock">
                    <button onClick={SignOut} id="exiteBtn">Выйти</button>
                </div>

                <div className="employeeData">
                    <div id="employeeblock">
                        <div className="employeestatic">Сотрудник:</div>
                        <div className="employeeLabels">
                            {loadingUser ? 'загрузка...' : (errUser || userData.Name)}
                        </div>
                    </div>

                    <div id="positionblock">
                        <div className="employeestatic">Должность:</div>
                        <div className="employeeLabels">
                            {loadingUser ? 'загрузка...' : userData.userPosition}
                        </div>
                    </div>

                    {/* Основная ставка */}
                    <div id="rateblock">
                        <div className="employeestatic">Основная ставка:</div>
                        <div id="ratelabel">
                            {loadingSalary ? 'загрузка' : `${fmtNum(userSalary.Rate)} ₽/л`}
                        </div>
                    </div>

                    {/* Ставка за наличные — показываем только если есть спецставка */}
                    {Number(userSalary?.isSpecialRateForCash || 0) === 1 && Number(userSalary?.SpecialRateForCash || 0) > 0 && (
                        <div id="rateblock">
                            <div className="employeestatic">Ставка за наличные:</div>
                            <div id="ratelabel">
                                {loadingSalary ? 'загрузка' : `${fmtNum(userSalary.SpecialRateForCash)} ₽/л`}
                            </div>
                        </div>
                    )}

                    {hasDebt && (
                        <div id="messageblock">
                            <div id="debtmessage">Долг за пред. месяц:</div>
                            <div id="debtValue">{fmtRub(userData.TotalDebt)}</div>
                        </div>
                    )}
                </div>

                <div className="salaryData">
                    <div className="datepicker">
                        <div id="previousMonth">
                            <button onClick={prevMonthClicked} type="button" className={prevBtnClass} id="btn11">
                                Прошлый месяц
                            </button>
                        </div>
                        <div id="thisMonth">
                            <button onClick={currentMonthClicked} type="button" className={currBtnClass} id="btn22">
                                Текущий месяц
                            </button>
                        </div>
                    </div>

                    <div id="month">{monthLabel}</div>

                    <div className="mainSalaryData">
                        {/* Литры безналом */}
                        <div className="SalaryDataBlockStatic">
                            <div className="mainSalaryStatic">Литры безналом:</div>
                            <div className="litrsSalaryBonusLabels">
                                {loadingSalary ? 'загрузка' : fmtNum(userSalary.LitresNonCash)}
                            </div>
                        </div>

                        {/* Литры наличными */}
                        <div className="SalaryDataBlockStatic">
                            <div className="mainSalaryStatic">Литры наличными:</div>
                            <div className="litrsSalaryBonusLabels">
                                {loadingSalary ? 'загрузка' : fmtNum(userSalary.LitresCash)}
                            </div>
                        </div>

                        {/* Итог литров (если хотите оставить общий показатель) */}
                        <div className="SalaryDataBlockStatic">
                            <div className="mainSalaryStatic">Литров итого:</div>
                            <div className="litrsSalaryBonusLabels">
                                {loadingSalary ? 'загрузка' : fmtNum(userSalary.Litres)}
                            </div>
                        </div>

                        <div className="SalaryDataBlockStatic">
                            <div className="mainSalaryStatic">Зарплата:</div>
                            <div className="litrsSalaryBonusLabels">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.Salary)}
                            </div>
                        </div>

                        <div className="SalaryDataBlockStatic">
                            <div className="mainSalaryStatic">Премия:</div>
                            <div className="litrsSalaryBonusLabels">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.Bonus)}
                            </div>
                        </div>

                        <div id="prepaymentblock">
                            <div className="mainSalaryStatic">Авансы:</div>
                            <div id="prepaymentlabel">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.Prepayment)}
                            </div>
                        </div>

                        <div className="feeAndMinusBlock">
                            <div className="mainSalaryStatic">Штрафы:</div>
                            <div className="feeAndMinuslabels">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.Fees)}
                            </div>
                        </div>

                        <div className="feeAndMinusBlock">
                            <div className="mainSalaryStatic">Минусы:</div>
                            <div className="feeAndMinuslabels">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.Minus)}
                            </div>
                        </div>

                        <div id="debtblock">
                            <div className="mainSalaryStatic">Задолжность:</div>
                            <div id="debtlabel">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.Debt)}
                            </div>
                        </div>

                        <div id="totalSalaryblock">
                            <div id="totalSalary">Итого:</div>
                            <div id="totalSalarylabel">
                                {loadingSalary ? 'загрузка' : fmtRub(userSalary.TotalSalary)}
                            </div>
                        </div>

                        {/* Inline errors for clarity */}
                        {(errSalary || errLogs) && (
                            <div style={{ color: 'crimson', marginTop: 8 }}>
                                {errSalary && <div>{errSalary}</div>}
                                {errLogs && <div>{errLogs}</div>}
                            </div>
                        )}
                    </div>
                </div>

                {loadingUser ? (
                    <div>Загрузка ...</div>
                ) : (
                    <Rules userPosition={userData.userPosition} />
                )}

                <div id="UserLogsTitle">Записи за месяц:</div>

                {loadingLogs ? (
                    <div>Загрузка ...</div>
                ) : (
                    userLogs.map((log, idx) => <UserlogItem data={log} key={idx} />)
                )}
            </div>
        </div>
    );
}

export default Home;